import React, { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Settings,
  ArrowLeft,
} from "lucide-react";

interface DashboardLayoutProps {
  children: ReactNode;
  title: string;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, title }) => {
  const location = useLocation();
  const currentPath = location.pathname;

  const navItems = [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: <LayoutDashboard className="h-5 w-5" />,
    },
    {
      name: "Produtos",
      href: "/dashboard/products",
      icon: <Package className="h-5 w-5" />,
    },
    {
      name: "Pedidos",
      href: "/dashboard/orders",
      icon: <ShoppingCart className="h-5 w-5" />,
    },
    {
      name: "Clientes",
      href: "/dashboard/customers",
      icon: <Users className="h-5 w-5" />,
    },
    {
      name: "Configurações",
      href: "/dashboard/settings",
      icon: <Settings className="h-5 w-5" />,
    },
  ];

  const isActive = (path: string) => {
    if (path === "/dashboard" && currentPath === "/dashboard") return true;
    if (path !== "/dashboard" && currentPath.startsWith(path)) return true;
    return false;
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 min-h-screen hidden md:block">
        <div className="p-4 border-b border-gray-200">
          <Link to="/" className="flex items-center">
            <h1 className="text-xl font-bold text-aqua-700">Aqua Cart</h1>
            <span className="ml-2 text-xs bg-aqua-100 text-aqua-700 px-2 py-0.5 rounded-full">
              Dashboard
            </span>
          </Link>
        </div>
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                isActive(item.href)
                  ? "bg-aqua-50 text-aqua-700"
                  : "text-gray-600 hover:bg-gray-50 hover:text-aqua-700"
              }`}
            >
              <span className="mr-3">{item.icon}</span>
              {item.name}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-200 mt-auto">
          <Link
            to="/"
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900"
          >
            <span className="mr-3">🏠</span>
            Voltar para a loja
          </Link>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile header */}
        <div className="md:hidden bg-white p-4 border-b border-gray-200 flex items-center justify-between">
          <h1 className="text-xl font-bold text-aqua-700">Aqua Cart</h1>
          <span className="text-xs bg-aqua-100 text-aqua-700 px-2 py-0.5 rounded-full">
            Dashboard
          </span>
        </div>

        {/* Content */}
        <main className="flex-1 p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            <Link
              to="/"
              className="text-sm flex items-center text-gray-600 hover:text-gray-900 md:hidden"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Voltar
            </Link>
          </div>
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout; 